import React, { useState } from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { canManageOrganizationMember } from "@toolkit/mobile-shared";
import type { MainAppPageProps, MembershipRole, OrgMember } from "../types";

type RemoveOrganizationMemberFormProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes"
> & {
  member: OrgMember;
  actorRole: MembershipRole;
  isCurrentUser: boolean;
};

function memberAction(
  template: string | undefined,
  membershipId: string,
  fallback: string,
) {
  return (template ?? fallback).replace(":id", membershipId);
}

export const RemoveOrganizationMemberForm: React.FC<
  RemoveOrganizationMemberFormProps
> = ({ csrfToken, routes, member, actorRole, isCurrentUser }) => {
  const [open, setOpen] = useState(false);

  if (!canManageOrganizationMember(actorRole, member.role, isCurrentUser)) {
    return null;
  }

  const action = memberAction(
    routes.destroyOrganizationMember,
    member.id,
    `/organization/members/${member.id}`,
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
      >
        Remove
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {member.name}?</DialogTitle>
            <DialogDescription>
              They will lose access to this company. Job sites, photos, and
              other company content they added will stay. They will need a new
              invite to rejoin.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <form action={action} method="post">
              <AuthenticityTokenField csrfToken={csrfToken} />
              <input type="hidden" name="_method" value="delete" />
              <Button type="submit" variant="destructive">
                Remove from company
              </Button>
            </form>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
